import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { theme, media } from './theme';
import CzechFlag from './assets/icons/czech-flag.png';
import UKFlag from './assets/flags/EN.png';
import GermanFlag from './assets/flags/DE.png';
import SlovakFlag from './assets/flags/SK.png';
import IbisGoldLogo from './assets/graphics/ibisingold.png';
import FireGoldLogo from './assets/graphics/firegold.png';
import bgImage from './assets/graphics/bg.jpg';

import flag_2 from './assets/flags/flag_2.png';
import flag_3 from './assets/flags/flag_3.png';
import flag_4 from './assets/flags/flag_4.png';
import flag_5 from './assets/flags/flag_5.png';
import flag_6 from './assets/flags/flag_6.png';
import flag_7 from './assets/flags/flag_7.png';
import flag_8 from './assets/flags/flag_8.png';
import flag_9 from './assets/flags/flag_9.png';
import flag_10 from './assets/flags/flag_10.png';

// Styled components
const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100svh;
  background-image: url(${bgImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  color: ${theme.colors.white};
  padding: 6rem;

  ${media.tablet} {
    padding: ${theme.spacing.xl} ${theme.spacing.lg};
  }
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: ${theme.spacing.xxl};
  z-index: ${theme.zIndex.base};
`;

const LogoImg = styled.img`
  height: ${theme.sizes.logoHeightMd};

  ${media.mobileLg} {
    height: ${theme.sizes.logoHeightMobile};
  }
`;

const Options = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 215px);
  justify-content: center;
  gap: 25px;

  ${media.desktopLg} {
    grid-template-columns: repeat(4, 215px);
  }

  ${media.desktop} {
    grid-template-columns: repeat(3, 215px);
  }

  ${media.tabletLg} {
    grid-template-columns: repeat(2, 215px);
  }

  ${media.mobile} {
    grid-template-columns: repeat(1, 215px);
  }
`;

const OptionButton = styled.button`
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.26;
    cursor: initial;
  }
`;

const FlagImg = styled.img`
  width: 215px;
  height: 143px;
  object-fit: cover;
`;

export const LANGUAGES = {
  CZ: 'cs',
  SK: 'sk',
  EN: 'en',
  DE: 'de'
} as const;

/**
 * Typ pro jazykový kód
 */
export type LanguageCode = typeof LANGUAGES[keyof typeof LANGUAGES] | number;

/**
 * Struktura pro jeden jazyk/vlajku
 */
interface Language {
  code: LanguageCode;
  label: string;
  flag: string;
  isEnabled: boolean;
}

/**
 * Props pro LanguageSelector komponentu
 */
interface LanguageSelectorProps {
  /** Callback volaný při výběru jazyka */
  onSelectLanguage: (code: LanguageCode) => void;
}

/**
 * Komponenta pro výběr jazyka s vlajkami
 */
const LanguageSelector = ({ onSelectLanguage }: LanguageSelectorProps): React.ReactElement => {
  const { i18n } = useTranslation();

  const languages: readonly Language[] = [
    { code: LANGUAGES.CZ, label: 'Vyberte jazyk', flag: CzechFlag, isEnabled: true },
    { code: LANGUAGES.SK, label: 'Vyberte jazyk', flag: SlovakFlag, isEnabled: true },
    { code: LANGUAGES.EN, label: 'Select a language', flag: UKFlag, isEnabled: false },
    { code: LANGUAGES.DE, label: 'Wählen Sie die Sprache', flag: GermanFlag, isEnabled: false },
    { code: 11, label: '', flag: flag_2, isEnabled: false },
    { code: 12, label: '', flag: flag_3, isEnabled: false },
    { code: 13, label: '', flag: flag_4, isEnabled: false },
    { code: 14, label: '', flag: flag_5, isEnabled: false },
    { code: 15, label: '', flag: flag_6, isEnabled: false },
    { code: 16, label: '', flag: flag_7, isEnabled: false },
    { code: 17, label: '', flag: flag_8, isEnabled: false },
    { code: 18, label: '', flag: flag_9, isEnabled: false },
    { code: 19, label: '', flag: flag_10, isEnabled: false },
  ] as const;

  const handleLanguageSelect = (code: LanguageCode) => {
    // Změnit jazyk v i18next
    i18n.changeLanguage(code as string);
    // Volat původní callback
    onSelectLanguage(code);
  };

  return (
    <Background>
      <Container>
        <Header>
          <LogoImg src={IbisGoldLogo} alt="IBISINGOLD" />
          <LogoImg src={FireGoldLogo} alt="FireGOLD" />
        </Header>

        <Options>
          {languages.map(({ code, label, flag, isEnabled }) => (
            <OptionButton
              disabled={!isEnabled}
              key={code}
              onClick={() => handleLanguageSelect(code)}>
              <FlagImg src={flag} alt={`${code} flag`} />
            </OptionButton>
          ))}
        </Options>
      </Container>
    </Background>
  );
};

export default LanguageSelector;
